import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  IconButton,
  Collapse,
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

const MAILBOX_FIELDS = [
  { label: "Mailbox Size", key: "MailboxSize" },
  { label: "Issue Warning Quota", key: "IssueWarningQuota" },
  { label: "Prohibit Send Quota", key: "ProhibitSendQuota" },
  { label: "Prohibit Send Receive Quota", key: "ProhibitSendReceiveQuota" },
  { label: "Archive Size", key: "ArchiveSize" },
  { label: "Mailbox Creation", key: "MailboxCreationDate" },
];

const ACCESS_FIELDS = [
  { label: "Email Forwarding", key: "EmailForwarding" },
  { label: "Send on Behalf", key: "SendOnBehalf" },
  { label: "Full Access", key: "FullAccess" },
  { label: "Send As", key: "SendAs" },
];

export const O365Exchange = () => {
  const [state, setState] = useState({
    data: null,
    isLoaded: false,
    error: null,
    outOfOfficeOpen: false,
  });

  const { authState } = useOktaAuth();
  const { userModule } = useContext(UserModuleContext);

  const fetchMailboxInfo = async () => {
    const endpoint = `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/o365/mailbox/info`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: authState.accessToken.accessToken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          networkId: userModule.userData["Network ID"],
        }),
      });

      if (!response.ok) {
        throw new Error(`User ${userModule.userId} not found in Office 365 APIs`);
      }

      const result = await response.json();
      setState((prev) => ({ ...prev, data: result.mailboxDetails, isLoaded: true }));
    } catch (error) {
      console.error(error);

      // Retry after 30s
      setTimeout(async () => {
        try {
          const retryResponse = await fetch(endpoint, {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              networkId: userModule.userData["Network ID"],
            }),
          });

          if (!retryResponse.ok) {
            throw new Error(`User ${userModule.userId} not found in Office 365 APIs`);
          }

          const retryResult = await retryResponse.json();
          setState((prev) => ({ ...prev, data: retryResult.mailboxDetails, isLoaded: true }));
        } catch (nestedError) {
          setState((prev) => ({ ...prev, error: nestedError, isLoaded: true }));
        }
      }, 30000);
    }
  };

  useEffect(() => {
    fetchMailboxInfo();
  }, [authState.accessToken.accessToken, userModule.userData]);

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Failed to load mailbox info
            </Typography>
            <Typography variant="body2">
              {state.error.message || "Unknown error occurred"}
            </Typography>
          </Box>
        </TableContainer>
    );
  }

  const renderCell = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {value.map((v) => (
                <Chip
                    key={v}
                    label={v}
                    size="small"
                    sx={{
                      backgroundColor: "#fdf0f2",
                      border: `1px solid ${WSU_RED}`,
                      color: WSU_RED,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                />
            ))}
          </Box>
      ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
      );
    }

    const display = value !== null && value !== undefined ? value.toString() : null;

    return display ? (
        <Typography variant="body2">{display}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  const data = state.data;

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* ── Section: Mailbox ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Mailbox
          </Typography>
        </Box>

        <Table>
          <TableBody>
            {MAILBOX_FIELDS.map(({ label, key }) => (
                <TableRow key={key}>
                  <TableCell sx={labelCellSx}>{label}</TableCell>
                  <TableCell>{renderCell(data[key])}</TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Sending Restrictions ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Sending Restrictions
          </Typography>
        </Box>

        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={labelCellSx}>Blocked From Sending Email</TableCell>
              <TableCell>{renderCell(data["UserBlockedFromSendingEmail"])}</TableCell>
            </TableRow>
            {data["UserBlockedFromSendingEmail"] && (
                <TableRow>
                  <TableCell sx={labelCellSx}>Reason Blocked</TableCell>
                  <TableCell>{renderCell(data["ReasonUserIsBlocked"])}</TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Access & Delegation ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Access &amp; Delegation
          </Typography>
        </Box>

        <Table>
          <TableBody>
            {ACCESS_FIELDS.map(({ label, key }) => (
                <TableRow key={key}>
                  <TableCell sx={labelCellSx}>{label}</TableCell>
                  <TableCell>{renderCell(data[key])}</TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Out of Office ── */}
        <Box
            sx={{ px: 3, pt: 3, pb: 1, display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
            onClick={() => setState((prev) => ({ ...prev, outOfOfficeOpen: !prev.outOfOfficeOpen }))}
        >
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5, flex: 1 }}>
            Out of Office
          </Typography>
          <IconButton size="small" sx={{ color: WSU_RED }}>
            {state.outOfOfficeOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>

        <Collapse in={state.outOfOfficeOpen} timeout="auto" unmountOnExit>
          <Table>
            <TableBody>
              {Object.entries(data["OutOfOffice"] || {}).map(([key, value]) => (
                  <TableRow key={`out-of-office-${key}`}>
                    <TableCell sx={labelCellSx}>{key}</TableCell>
                    <TableCell>{renderCell(value)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ pb: 2 }} />
        </Collapse>

      </TableContainer>
  );
};